import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useImpersonation } from "@/contexts/ImpersonationContext";

const sb = supabase as unknown as SupabaseClient<any>;

export const useEntitlement = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const { viewAsUser } = useImpersonation();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => setUserId(session?.user?.id ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ["user-admin", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await sb
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin");
      if (error) throw error;
      return !!data && data.length > 0;
    },
  });

  const { data: entitlement, isLoading } = useQuery({
    queryKey: ["entitlement", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await sb
        .from("user_exam_access_safe")
        .select("exam_slug, expires_at, trial_started_at, trial_expires_at, subscription_type, has_stripe_customer")
        .eq("user_id", userId)
        .order("expires_at", { ascending: false, nullsFirst: false });
      if (error) throw error;

      const rows = (data || []) as Array<{ 
        exam_slug: string | null; 
        expires_at: string | null;
        trial_started_at: string | null;
        trial_expires_at: string | null;
        subscription_type: string | null;
        has_stripe_customer: boolean;
      }>;

      const now = new Date();
      return rows.find((row) => {
        const hasTrialAccess = row.trial_expires_at && new Date(row.trial_expires_at) > now;
        const hasPaidAccess = row.expires_at && new Date(row.expires_at) > now;
        return !!(hasTrialAccess || hasPaidAccess);
      }) ?? rows[0] ?? null;
    },
  });

  const examSlug = entitlement?.exam_slug ?? null;
  const expiresAt = entitlement?.expires_at ?? null;
  const trialStartedAt = entitlement?.trial_started_at ?? null;
  const trialExpiresAt = entitlement?.trial_expires_at ?? null;
  const subscriptionType = entitlement?.subscription_type ?? null;
  const hasStripeCustomer = entitlement?.has_stripe_customer ?? false;

  const { data: labelRow } = useQuery({
    queryKey: ["exam-label-id", examSlug],
    enabled: !!examSlug,
    queryFn: async () => {
      const { data, error } = await sb
        .from("exam_labels")
        .select("id, slug")
        .eq("slug", examSlug)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; slug: string } | null;
    },
  });

  // Check if user has active access (trial or paid)
  const hasTrialAccess = examSlug && trialExpiresAt && new Date(trialExpiresAt) > new Date();
  const hasPaidAccess = examSlug && expiresAt && new Date(expiresAt) > new Date();
  const realHasActiveAccess = !!(hasTrialAccess || hasPaidAccess);

  const realIsAdmin = !!isAdmin;
  // Real admins always have full content access (DB RLS already allows them).
  // This keeps the UI unlocked while impersonating, so they see exactly what a paid user sees.
  const hasActiveAccess = realHasActiveAccess || realIsAdmin;

  return {
    loading: isLoading,
    userId,
    examSlug,
    labelId: labelRow?.id ?? null,
    expiresAt,
    trialStartedAt,
    trialExpiresAt,
    subscriptionType,
    hasStripeCustomer,
    // When admin is "viewing as user", mask admin powers everywhere.
    isAdmin: realIsAdmin && !viewAsUser,
    // Real underlying role — used only by the impersonation toggle UI.
    isRealAdmin: realIsAdmin,
    isImpersonating: realIsAdmin && viewAsUser,
    hasActiveAccess,
  } as const;
};
